import { useState, useEffect } from 'react';
import { X, User, Mail, Phone, MapPin, Heart, Shield } from 'lucide-react';
import toast from 'react-hot-toast';
import { patientsApi, Patient, CreatePatientRequest } from '../../lib/api';

interface EditPatientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatientUpdated: () => void;
  patient: Patient | null;
}

const EditPatientModal = ({ isOpen, onClose, onPatientUpdated, patient }: EditPatientModalProps) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreatePatientRequest>({
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    dateOfBirth: '',
    gender: 'MALE',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US'
    },
    emergencyContact: {
      name: '',
      relationship: '',
      phone: '',
      email: ''
    },
    insuranceInfo: {
      provider: '',
      policyNumber: '',
      groupNumber: '',
      expiryDate: ''
    }
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when patient prop changes
  useEffect(() => {
    if (patient) {
      setFormData({
        email: patient.user.email,
        firstName: patient.user.firstName,
        lastName: patient.user.lastName,
        phone: patient.user.phone,
        dateOfBirth: patient.dateOfBirth.split('T')[0], // Convert to YYYY-MM-DD format
        gender: patient.gender,
        address: patient.address || {
          street: '',
          city: '',
          state: '',
          zipCode: '',
          country: 'US'
        },
        emergencyContact: patient.emergencyContact || {
          name: '',
          relationship: '',
          phone: '',
          email: ''
        },
        insuranceInfo: patient.insuranceInfo || {
          provider: '',
          policyNumber: '',
          groupNumber: '',
          expiryDate: ''
        }
      });
    }
  }, [patient]);

  const validateStep = (step: number): boolean => {
    const newErrors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
      if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
      
      // Email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (formData.email && !emailRegex.test(formData.email)) {
        newErrors.email = 'Invalid email format';
      }
    }

    if (step === 2) {
      if (!formData.address.street.trim()) newErrors.street = 'Street address is required';
      if (!formData.address.city.trim()) newErrors.city = 'City is required';
      if (!formData.address.state.trim()) newErrors.state = 'State is required';
      if (!formData.address.zipCode.trim()) newErrors.zipCode = 'ZIP code is required';
    }

    if (step === 3) {
      if (!formData.emergencyContact.name.trim()) newErrors.emergencyContactName = 'Emergency contact name is required';
      if (!formData.emergencyContact.relationship.trim()) newErrors.emergencyContactRelationship = 'Relationship is required';
      if (!formData.emergencyContact.phone.trim()) newErrors.emergencyContactPhone = 'Emergency contact phone is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep) || !patient) return;

    setLoading(true);
    try {
      await patientsApi.updatePatient(patient.id, formData);
      toast.success('Patient updated successfully!');
      onPatientUpdated();
      onClose();
      setCurrentStep(1);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to update patient';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof CreatePatientRequest] as any || {}),
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleClose = () => {
    setCurrentStep(1);
    setErrors({});
    onClose();
  };

  if (!isOpen || !patient) return null;

  const steps = [
    { number: 1, title: 'Personal Info', icon: User },
    { number: 2, title: 'Address', icon: MapPin },
    { number: 3, title: 'Emergency Contact', icon: Heart },
    { number: 4, title: 'Insurance', icon: Shield }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Edit Patient</h2>
            <p className="text-sm text-gray-500 mt-1">
              {patient.user.firstName} {patient.user.lastName} (#{patient.patientNumber})
            </p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-4 border-b bg-gray-50">
          <div className="flex items-center justify-between">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = currentStep === step.number;
              const isCompleted = currentStep > step.number;
              
              return (
                <div key={step.number} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                    isActive ? 'bg-primary-600 text-white' :
                    isCompleted ? 'bg-green-600 text-white' :
                    'bg-gray-300 text-gray-600'
                  }`}>
                    <StepIcon className="h-4 w-4" />
                  </div>
                  <span className={`ml-2 text-sm font-medium ${
                    isActive ? 'text-primary-600' :
                    isCompleted ? 'text-green-600' :
                    'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                  {index < steps.length - 1 && (
                    <div className={`mx-4 h-px w-12 ${
                      isCompleted ? 'bg-green-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 overflow-y-auto max-h-96">
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">First Name *</label>
                  <input
                    type="text"
                    className={`input ${errors.firstName ? 'input-error' : ''}`}
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                  />
                  {errors.firstName && <p className="form-error">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="form-label">Last Name *</label>
                  <input
                    type="text"
                    className={`input ${errors.lastName ? 'input-error' : ''}`}
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                  />
                  {errors.lastName && <p className="form-error">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className={`input ${errors.email ? 'input-error' : ''}`}
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="Enter email address"
                />
                {errors.email && <p className="form-error">{errors.email}</p>}
              </div>

              <div>
                <label className="form-label">Phone *</label>
                <input
                  type="tel"
                  className={`input ${errors.phone ? 'input-error' : ''}`}
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Enter phone number"
                />
                {errors.phone && <p className="form-error">{errors.phone}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Date of Birth *</label>
                  <input
                    type="date"
                    className={`input ${errors.dateOfBirth ? 'input-error' : ''}`}
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  />
                  {errors.dateOfBirth && <p className="form-error">{errors.dateOfBirth}</p>}
                </div>
                <div>
                  <label className="form-label">Gender *</label>
                  <select
                    className="input"
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  >
                    <option value="MALE">Male</option>
                    <option value="FEMALE">Female</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Address */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label className="form-label">Street Address *</label>
                <input
                  type="text"
                  className={`input ${errors.street ? 'input-error' : ''}`}
                  value={formData.address.street}
                  onChange={(e) => handleInputChange('address.street', e.target.value)}
                  placeholder="Enter street address"
                />
                {errors.street && <p className="form-error">{errors.street}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">City *</label>
                  <input
                    type="text"
                    className={`input ${errors.city ? 'input-error' : ''}`}
                    value={formData.address.city}
                    onChange={(e) => handleInputChange('address.city', e.target.value)}
                    placeholder="Enter city"
                  />
                  {errors.city && <p className="form-error">{errors.city}</p>}
                </div>
                <div>
                  <label className="form-label">State *</label>
                  <input
                    type="text"
                    className={`input ${errors.state ? 'input-error' : ''}`}
                    value={formData.address.state}
                    onChange={(e) => handleInputChange('address.state', e.target.value)}
                    placeholder="Enter state"
                  />
                  {errors.state && <p className="form-error">{errors.state}</p>}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">ZIP Code *</label>
                  <input
                    type="text"
                    className={`input ${errors.zipCode ? 'input-error' : ''}`}
                    value={formData.address.zipCode}
                    onChange={(e) => handleInputChange('address.zipCode', e.target.value)}
                    placeholder="Enter ZIP code"
                  />
                  {errors.zipCode && <p className="form-error">{errors.zipCode}</p>}
                </div>
                <div>
                  <label className="form-label">Country</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.address.country}
                    onChange={(e) => handleInputChange('address.country', e.target.value)}
                    placeholder="Enter country"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Emergency Contact */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div>
                <label className="form-label">Contact Name *</label>
                <input
                  type="text"
                  className={`input ${errors.emergencyContactName ? 'input-error' : ''}`}
                  value={formData.emergencyContact.name}
                  onChange={(e) => handleInputChange('emergencyContact.name', e.target.value)}
                  placeholder="Enter emergency contact name"
                />
                {errors.emergencyContactName && <p className="form-error">{errors.emergencyContactName}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Relationship *</label>
                  <select
                    className={`input ${errors.emergencyContactRelationship ? 'input-error' : ''}`}
                    value={formData.emergencyContact.relationship}
                    onChange={(e) => handleInputChange('emergencyContact.relationship', e.target.value)}
                  >
                    <option value="">Select relationship</option>
                    <option value="Spouse">Spouse</option>
                    <option value="Parent">Parent</option>
                    <option value="Child">Child</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Friend">Friend</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.emergencyContactRelationship && <p className="form-error">{errors.emergencyContactRelationship}</p>}
                </div>
                <div>
                  <label className="form-label">Phone *</label>
                  <input
                    type="tel"
                    className={`input ${errors.emergencyContactPhone ? 'input-error' : ''}`}
                    value={formData.emergencyContact.phone}
                    onChange={(e) => handleInputChange('emergencyContact.phone', e.target.value)}
                    placeholder="Enter phone number"
                  />
                  {errors.emergencyContactPhone && <p className="form-error">{errors.emergencyContactPhone}</p>}
                </div>
              </div>

              <div>
                <label className="form-label">Email (Optional)</label>
                <input
                  type="email"
                  className="input"
                  value={formData.emergencyContact.email}
                  onChange={(e) => handleInputChange('emergencyContact.email', e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
            </div>
          )}

          {/* Step 4: Insurance Information */}
          {currentStep === 4 && (
            <div className="space-y-4">
              <div>
                <label className="form-label">Insurance Provider (Optional)</label>
                <input
                  type="text"
                  className="input"
                  value={formData.insuranceInfo?.provider || ''}
                  onChange={(e) => handleInputChange('insuranceInfo.provider', e.target.value)}
                  placeholder="Enter insurance provider"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="form-label">Policy Number (Optional)</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.insuranceInfo?.policyNumber || ''}
                    onChange={(e) => handleInputChange('insuranceInfo.policyNumber', e.target.value)}
                    placeholder="Enter policy number"
                  />
                </div>
                <div>
                  <label className="form-label">Group Number (Optional)</label>
                  <input
                    type="text"
                    className="input"
                    value={formData.insuranceInfo?.groupNumber || ''}
                    onChange={(e) => handleInputChange('insuranceInfo.groupNumber', e.target.value)}
                    placeholder="Enter group number"
                  />
                </div>
              </div>

              <div>
                <label className="form-label">Expiry Date (Optional)</label>
                <input
                  type="date"
                  className="input"
                  value={formData.insuranceInfo?.expiryDate || ''}
                  onChange={(e) => handleInputChange('insuranceInfo.expiryDate', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t bg-gray-50">
          <div>
            {currentStep > 1 && (
              <button
                onClick={handlePrevious}
                className="btn-secondary"
                disabled={loading}
              >
                Previous
              </button>
            )}
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            
            {currentStep < 4 ? (
              <button
                onClick={handleNext}
                className="btn-primary"
                disabled={loading}
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Updating Patient...' : 'Update Patient'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPatientModal; 
